/**
 * SOVRANOVA — Sleep Protocol Player
 *
 * 8-hour sleep audio engine:
 *   - 432 Hz base tone (sine wave, ultra-low volume)
 *   - Binaural delta entrainment: 2 Hz offset between L/R channels (delta = deep sleep)
 *   - Slow breathing guide for sleep onset (4-7-8 pattern)
 *   - Lock-screen friendly: minimal UI, dark, no unnecessary light
 *   - Alarm at completion (optional)
 */
import { useState, useEffect, useRef } from 'react'
import { useWakeLock } from '../../hooks/useSessionResilience'
import { DimVeil } from '../common/DimVeil'

const DEFAULT_HOURS = 8
const BREATH_PHASES = [
  { label: 'Breathe in... 4',  ms: 4000 },
  { label: 'Hold... 7',        ms: 7000 },
  { label: 'Release... 8',     ms: 8000 },
]
const CYCLE_MS = BREATH_PHASES.reduce((s, p) => s + p.ms, 0) // 19 000 ms

function createSleepEngine(hz = 432) {
  let ctx = null, oscL = null, oscR = null, gainL = null, gainR = null, merger = null

  return {
    start() {
      if (ctx) return
      ctx = new (window.AudioContext || window.webkitAudioContext)()
      merger = ctx.createChannelMerger(2)

      // Left channel: 432 Hz
      oscL = ctx.createOscillator(); gainL = ctx.createGain()
      oscL.type = 'sine'; oscL.frequency.setValueAtTime(hz, ctx.currentTime)
      gainL.gain.setValueAtTime(0, ctx.currentTime)
      gainL.gain.linearRampToValueAtTime(0.08, ctx.currentTime + 5) // very quiet
      oscL.connect(gainL); gainL.connect(merger, 0, 0)

      // Right channel: 434 Hz (2 Hz binaural delta)
      oscR = ctx.createOscillator(); gainR = ctx.createGain()
      oscR.type = 'sine'; oscR.frequency.setValueAtTime(hz + 2, ctx.currentTime)
      gainR.gain.setValueAtTime(0, ctx.currentTime)
      gainR.gain.linearRampToValueAtTime(0.08, ctx.currentTime + 5)
      oscR.connect(gainR); gainR.connect(merger, 0, 1)

      merger.connect(ctx.destination)
      oscL.start(); oscR.start()
    },
    stop() {
      if (!ctx) return
      ;[gainL, gainR].forEach(g => {
        if (!g) return
        g.gain.cancelScheduledValues(ctx.currentTime)
        g.gain.setValueAtTime(g.gain.value, ctx.currentTime)
        g.gain.linearRampToValueAtTime(0, ctx.currentTime + 3)
      })
      setTimeout(() => {
        try { oscL?.stop(); oscR?.stop(); ctx?.close() } catch {}
        ctx = oscL = oscR = gainL = gainR = merger = null
      }, 3500)
    },
  }
}

export function SleepPlayer({ hours = DEFAULT_HOURS, onClose }) {
  const TOTAL_MS = hours * 60 * 60 * 1000

  const [elapsed,    setElapsed]    = useState(0)
  const [paused,     setPaused]     = useState(false)
  const [done,       setDone]       = useState(false)
  const [phaseIdx,   setPhaseIdx]   = useState(0)
  const [breathCycles, setBreathCycles] = useState(0)
  const [audioErr,   setAudioErr]   = useState(false)

  const engineRef     = useRef(null)
  const timerRef      = useRef(null)
  const phaseTimerRef = useRef(null)

  // A screen timeout suspends the Web Audio graph on mobile — the delta tone
  // simply stops mid-night and the "8-hour protocol" silently becomes a
  // 2-minute one (user request 19 Aug: active features must not time out).
  // Wake lock holds the screen; the DimVeil below fades it to near-black
  // within seconds so it neither lights the room nor drains the battery.
  useWakeLock(!done && !paused)

  // Breathing: stop after 10 cycles (~3 min) so user can fall asleep
  const breathingActive = breathCycles < 10 && !done

  useEffect(() => {
    try {
      engineRef.current = createSleepEngine(432)
      engineRef.current.start()
    } catch { setAudioErr(true) }

    timerRef.current = setInterval(() => {
      setElapsed(prev => {
        const next = prev + 1000
        if (next >= TOTAL_MS) {
          clearInterval(timerRef.current)
          setDone(true)
          engineRef.current?.stop()
          return TOTAL_MS
        }
        return next
      })
    }, 1000)

    return () => {
      clearInterval(timerRef.current)
      clearTimeout(phaseTimerRef.current)
      engineRef.current?.stop()
    }
  }, []) // eslint-disable-line

  // Breathing phase cycle — stops after 10 cycles
  useEffect(() => {
    if (!breathingActive || paused) return
    const phase = BREATH_PHASES[phaseIdx]
    phaseTimerRef.current = setTimeout(() => {
      const nextIdx = (phaseIdx + 1) % BREATH_PHASES.length
      if (nextIdx === 0) setBreathCycles(c => c + 1)
      setPhaseIdx(nextIdx)
    }, phase.ms)
    return () => clearTimeout(phaseTimerRef.current)
  }, [phaseIdx, breathingActive, paused])

  const togglePause = () => {
    setPaused(p => {
      if (!p) {
        clearInterval(timerRef.current)
      } else {
        timerRef.current = setInterval(() => {
          setElapsed(prev => {
            const next = prev + 1000
            if (next >= TOTAL_MS) { clearInterval(timerRef.current); setDone(true); engineRef.current?.stop(); return TOTAL_MS }
            return next
          })
        }, 1000)
      }
      return !p
    })
  }

  const remaining    = Math.max(0, TOTAL_MS - elapsed)
  const remHr        = Math.floor(remaining / 3600000)
  const remMin       = Math.floor((remaining % 3600000) / 60000)
  const pct          = Math.min(100, (elapsed / TOTAL_MS) * 100)

  return (
    <div style={s.page}>
      {/* Near-black veil after 15s idle — the screen is being held awake for
          the whole night, so it must go dark fast and stay dark. */}
      <DimVeil active={!done && !paused} idleMs={15000} hint="Sleep protocol running — tap to wake the screen" />
      <button style={s.back} onClick={() => { engineRef.current?.stop(); onClose?.() }}>← Back</button>

      <div style={s.header}>
        <span style={s.emoji}>🌙</span>
        <p style={s.tag}>SLEEP PROTOCOL · {hours} HOURS</p>
        <h1 style={s.title}>Sleep Healing</h1>
        <p style={s.subtitle}>Active frequency: 432 Hz · Delta binaural 2 Hz</p>
      </div>

      {/* Breathing guide — shows for first ~3 minutes */}
      {breathingActive && !done ? (
        <div style={s.breathCircle}>
          <div style={{
            ...s.breathInner,
            transform: phaseIdx === 0 ? 'scale(1.2)' : phaseIdx === 1 ? 'scale(1.2)' : 'scale(0.85)',
          }}>
            <p style={s.breathLabel}>{BREATH_PHASES[phaseIdx].label}</p>
          </div>
        </div>
      ) : (
        <div style={s.restCircle}>
          <p style={s.restLabel}>{done ? '✓ Complete' : paused ? 'Paused' : '● Healing'}</p>
        </div>
      )}

      {/* Progress */}
      <div style={s.progressRow}>
        <span style={s.progressLabel}>Progress</span>
        <span style={s.progressLabel}>{pct.toFixed(1)}%</span>
      </div>
      <div style={s.progressTrack}>
        <div style={{ ...s.progressFill, width: `${pct}%` }} />
      </div>

      {/* Frequency */}
      <div style={s.freqBox}>
        <p style={s.freqTag}>ACTIVE FREQUENCIES</p>
        <p style={s.freqHz}>432 Hz · 434 Hz</p>
        <p style={s.freqSub}>Binaural delta entrainment · Deep sleep architecture</p>
      </div>

      {audioErr && <p style={s.audioErr}>Audio unavailable — allow audio in browser settings.</p>}

      {done ? (
        <div style={s.doneWrap}>
          <p style={s.doneTitle}>✨ Sleep Protocol Complete</p>
          <p style={s.doneSub}>Your healing session has completed.</p>
          <button style={s.doneBtn} onClick={() => { onClose?.() }}>Close ◎</button>
        </div>
      ) : (
        <button style={{ ...s.pauseBtn, ...(paused ? s.pauseBtnPaused : {}) }} onClick={togglePause}>
          {paused
            ? `Resume ▶  ·  ${remHr}h ${remMin}m remaining`
            : `Pause ⏸  ·  ${remHr}h ${remMin}m remaining`}
        </button>
      )}

      <p style={s.tip}>
        {breathingActive
          ? 'Breathe along to drift into sleep. The tone continues while you rest.'
          : done ? '' : 'Sleep. The frequency is working. You can close your eyes.'}
      </p>

      <style>{`
        @keyframes sleepPulse { 0%,100% { box-shadow:0 0 0 0 rgba(201,168,76,0.1) } 50% { box-shadow:0 0 0 16px rgba(201,168,76,0) } }
      `}</style>
    </div>
  )
}

const s = {
  // Organic night-ink (Board P1, 2026-08-13): coolest room in the app, kept
  // blue-accented for bedtime; base moves from purple-black to deep slate ink.
  page: {
    minHeight: '100vh',
    background: 'linear-gradient(170deg, #20242a 0%, #141719 100%)',
    display: 'flex', flexDirection: 'column', alignItems: 'center',
    padding: '1.5rem 1.25rem 2.5rem', gap: '1.25rem',
  },
  back: {
    alignSelf: 'flex-start', background: 'transparent', border: 'none',
    color: 'rgba(255,255,255,0.3)', fontSize: '0.875rem', cursor: 'pointer',
  },
  header: { textAlign: 'center', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '0.35rem' },
  emoji: { fontSize: '2.25rem' },
  tag: { fontSize: '0.65rem', letterSpacing: '0.14em', color: '#7eb8c9', textTransform: 'uppercase', fontWeight: 600 },
  title: { fontSize: '1.6rem', fontWeight: 700, color: '#f5f0e8' },
  subtitle: { fontSize: '0.75rem', color: 'rgba(255,255,255,0.3)' },

  breathCircle: {
    width: 160, height: 160, borderRadius: '50%',
    border: '1px solid rgba(126,184,201,0.25)',
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    animation: 'sleepPulse 6s ease-in-out infinite',
  },
  breathInner: {
    width: 120, height: 120, borderRadius: '50%',
    background: 'linear-gradient(135deg, rgba(126,184,201,0.2), rgba(58,26,110,0.2))',
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    transition: 'transform 4s ease-in-out',
  },
  breathLabel: { fontSize: '0.8rem', color: '#c4e0e8', fontWeight: 500, textAlign: 'center', padding: '0 0.5rem' },

  restCircle: {
    width: 120, height: 120, borderRadius: '50%',
    border: '1px solid rgba(126,184,201,0.15)',
    background: 'rgba(126,184,201,0.05)',
    display: 'flex', alignItems: 'center', justifyContent: 'center',
  },
  restLabel: { fontSize: '0.75rem', color: 'rgba(196,224,232,0.6)', letterSpacing: '0.08em' },

  progressRow: {
    width: '100%', maxWidth: 520,
    display: 'flex', justifyContent: 'space-between',
    fontSize: '0.7rem', color: 'rgba(255,255,255,0.3)',
  },
  progressLabel: { fontSize: '0.7rem', color: 'rgba(255,255,255,0.3)' },
  progressTrack: { width: '100%', maxWidth: 520, height: 3, background: 'rgba(255,255,255,0.07)', borderRadius: 2, overflow: 'hidden' },
  progressFill: { height: '100%', background: 'linear-gradient(90deg, #7eb8c9, #9fcfe0)', borderRadius: 2, transition: 'width 1s linear' },

  freqBox: {
    width: '100%', maxWidth: 520,
    background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.06)',
    borderRadius: '1rem', padding: '0.875rem 1.25rem', textAlign: 'center',
  },
  freqTag: { fontSize: '0.6rem', letterSpacing: '0.16em', color: '#7eb8c9', textTransform: 'uppercase' },
  freqHz: { fontSize: '1.25rem', fontWeight: 700, color: '#f5f0e8', margin: '0.25rem 0' },
  freqSub: { fontSize: '0.72rem', color: 'rgba(255,255,255,0.3)' },
  audioErr: { fontSize: '0.72rem', color: '#e88', textAlign: 'center' },

  pauseBtn: {
    width: '100%', maxWidth: 520, padding: '0.875rem',
    background: 'rgba(126,184,201,0.12)', border: '1px solid rgba(126,184,201,0.25)',
    borderRadius: '999px', color: '#7eb8c9', fontSize: '0.9rem',
    fontWeight: 600, cursor: 'pointer', letterSpacing: '0.02em',
  },
  pauseBtnPaused: { background: 'rgba(126,184,201,0.2)', border: '1px solid rgba(126,184,201,0.4)' },

  doneWrap: { display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '0.5rem' },
  doneTitle: { fontSize: '1.1rem', fontWeight: 700, color: '#f5f0e8' },
  doneSub: { fontSize: '0.875rem', color: 'rgba(255,255,255,0.45)' },
  doneBtn: {
    marginTop: '0.75rem', padding: '0.75rem 2rem',
    background: 'rgba(126,184,201,0.15)', border: '1px solid rgba(126,184,201,0.35)',
    borderRadius: '999px', color: '#7eb8c9', fontSize: '0.95rem', fontWeight: 600, cursor: 'pointer',
  },
  tip: { fontSize: '0.78rem', color: 'rgba(255,255,255,0.2)', textAlign: 'center', maxWidth: 320, lineHeight: 1.6 },
}
